import type { ApiResponse } from './types';
import { getIdToken } from './auth';

/**
 * Cliente HTTP para a API SSR existente. Em dev, o Vite faz proxy dos prefixos
 * (/campaigns, /clients, ...) para app.pixeldmp.com injetando o cookie de sessão
 * (VITE_DEV_COOKIE). Em produção (beta.pixeldmp.com) as chamadas vão para a mesma
 * origem/gateway que expõe a API.
 */
export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

// Prefixo dedicado da API (proxied em dev, roteado pelo CDN em prod) — evita
// colisão com as rotas do SPA. Override via VITE_API_BASE se necessário.
const API_BASE = import.meta.env.VITE_API_BASE ?? '/_api';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const idToken = getIdToken();
  const res = await fetch(API_BASE + path, {
    credentials: 'include',
    redirect: 'manual', // 302 (login) não deve navegar/seguir — tratamos como não-autenticado
    headers: {
      'Content-Type': 'application/json',
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      ...(init?.headers || {}),
    },
    ...init,
  });

  if (res.status === 401 || res.status === 302 || res.type === 'opaqueredirect') {
    throw new ApiError('Not authenticated. Sign in at app.pixeldmp.com (or set VITE_DEV_COOKIE).', 401);
  }

  const text = await res.text();
  let json: unknown;
  try {
    json = text ? JSON.parse(text) : {};
  } catch {
    throw new ApiError(`Non-JSON response from ${path} (HTTP ${res.status})`, res.status);
  }

  if (!res.ok) {
    const msg = (json as ApiResponse<unknown>)?.error || (json as ApiResponse<unknown>)?.message || `HTTP ${res.status}`;
    throw new ApiError(msg, res.status);
  }
  return json as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', ...(body !== undefined ? { body: JSON.stringify(body) } : {}) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
